<template>
	<div
		v-focus
		:class="['teaser', 'teaser-large']"
		ref="teaserElement"
		@mouseenter="!isNativeDevice ? handleMouseEnter() : null"
		@mouseleave="!isNativeDevice ? handleMouseLeave() : null"
	>
		<!-- <transition :name="isNativeDevice ? '' : 'fade'"> -->
		<img
			ref="imageRef"
			@click="play()"
			v-if="!showVideo || !item.field_trailer?.uri"
			:src="currentSrc"
			:alt="item.field_image.meta.alt"
			:class="{ 'lazy-loaded': isLoaded }"
		/>
		<!-- </transition> -->
		<div class="preview-element" :class="{ 'is-web': !isNativeDevice }" ref="teaserElement" v-if="showPreviewSection">
			<transition :name="isNativeDevice ? '' : 'fade'">
				<div @click="play()" class="fade-container" :key="playerId">
					<div class="player-wrapper">
						<div
							ref="playerElement"
							:id="playerId"
							class="kwik-player kwik-player--large"
							autoplay
							muted
							playsinline
						></div>
					</div>
				</div>
			</transition>

			<Transition :name="isNativeDevice ? '' : 'fade'">
				<div class="preview-section" v-if="showPreviewSection">
					<ActionButtons
						v-if="!isNativeDevice && item"
						v-focus-section
						:item="item"
						:type="item.type"
						:player="`large-preview-${item.id}`"
						:episode_nid="selectedEpisodeNid1"
						:isHomeTeaser="true"
					/>
					<div v-if="item" class="preview-info">
						<div class="preview-info-duration">
							<h2 v-focus>
								{{ item.title }} <span v-if="episodeInfo">{{ episodeInfo }}</span>
							</h2>
							<div
								v-if="timeline !== undefined"
								class="video-timeline-progress"
								:style="{
									background: `linear-gradient(to right, #f13c36 0%, #f13c36 ${timeline}%, #ffffff ${timeline}%, #ffffff 100%)`,
								}"
							></div>
						</div>
						<p class="duration" v-if="item.field_duration">{{ item.field_duration }}</p>
					</div>
				</div>
			</Transition>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, onMounted, Ref, watch } from "vue"
	import { Movie } from "../../types/Movie"
	import { Series } from "../../types/Series"
	import { Episode } from "../../types/Episode"
	import { useMovieStore } from "../../stores/movies"
	import { useUserStore } from "../../stores/user"
	import ActionButtons from "../ActionButtons.vue"
	import { isNativeDevice, url } from "../../helper"
	import { useTeaserPreview } from "../../composables/useTeaserPreview"
	import { useLazyImage } from "../../composables/useLazyImage"
	import { useTeaserStore } from "../../stores/teaser"
	import { useUserProfiles } from "../../composables/useUserProfile"
	import api from "../../composables/useApi"
	import { extractDurationFromKwik } from "../../utils/media"
	import { useTimestampStore } from "../../stores/timestamps"
	import { useRouter } from "vue-router"
	import { useI18n } from "vue-i18n"
	import { usePlay } from "../../composables/usePlay"
	import { useDeviceManager } from "../../stores/deviceManager"
	import { Capacitor } from "@capacitor/core"
	const { playElement, selectedEpisodeNid } = usePlay()
	const { locale } = useI18n()
	const nextEpisodeId = ref<string | null>(null)
	const router = useRouter()
	const { fetchVideoFlagStoreByProfile } = useUserProfiles()
	const teaserStore = useTeaserStore()
	const showVideo = ref(false)
	const showPreviewSection = ref(false)
	const timestampStore = useTimestampStore()
	const entityType = ref("")
	const duration = ref(0)
	const episodeInfo = ref("")
	const selectedEpisodeNid1 = ref<number>(0)
	const deviceManager = useDeviceManager()

	// Generate unique player ID for each instance
	const props = withDefaults(
		defineProps<{
			item: Movie | Series
			isPreviewing: boolean
		}>(),
		{
			isPreviewing: false,
		}
	)

	const playerId = ref(`kwik-player-large-preview-${props.item?.id}`)
	const isInitializing = ref(false)
	const hoverTimeout = ref<number | undefined>(undefined)
	const isMobileDevice = computed(
		() =>
			deviceManager.operatingSystem === "ios" ||
			deviceManager.operatingSystem === "android" ||
			Capacitor.isNativePlatform()
		// () => deviceManager.deviceInfo.platform !== "web" || window.innerWidth <= 480
	)

	watch(
		() => showVideo.value,
		(newVal) => {
			if (!newVal) {
				// Pause the player when video is not shown
				if (typeof window.kwikMotion === "function" && window.kwikMotion(playerId.value)) {
					const player = window.kwikMotion(playerId.value)

					if (player) {
						isInitializing.value = false
						teaserStore.state.playerRefLarge = undefined
						player.dispose()
					}
				}
			}
		}
	)

	watch(
		() => showPreviewSection.value,
		async (newVal) => {
			// Clear any pending hover timeout
			if (hoverTimeout.value) {
				clearTimeout(hoverTimeout.value)
				hoverTimeout.value = undefined
			}

			if (newVal) {
				showVideo.value = true
				// Only init if not already initializing
				if (!isInitializing.value) {
					teaserStore.options.src = ""
					initPlayer()
					const entityId = props.item.type === "node--movie" ? (props.item as Movie)?.id : undefined
					const parentId = props.item?.type === "node--series" ? (props.item as Series).id : undefined
					const flag: Ref<string | undefined> = ref("")
					if (props.item?.type === "node--movie") {
						entityType.value = "movie"
						flag.value = "started"
					} else if (props.item?.type === "node--series") {
						entityType.value = "episode"
						flag.value = undefined
					}
					const res = await fetchVideoFlagStoreByProfile(entityId, entityType.value, flag.value, parentId)
					if (res.length && res[0].video_flag_type === "completed") {
						let episodes: string[] = []
						try {
							const res = await api.get(`/jsonapi/node/episode`, {
								params: {
									"filter[field_series.id]": parentId,
									"fields[node--episode]": "drupal_internal__nid",
								},
							})
							episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
						} catch (error) {
							console.error("Failed to fetch episodes:", error)
							return
						}
						const currentId = res[0]?.target_entity.drupal_internal__nid
						const currentIndex = episodes.indexOf(currentId)
						if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
							nextEpisodeId.value = episodes[currentIndex + 1]
							const response = await api.get(
								`${locale.value}/jsonapi/node/episode?jsonapi_include=1&include=field_trailer_file,field_image,field_series,field_series.field_image,field_subtitles,field_subtitles.field_subtitle_file`,
								{
									params: {
										"filter[drupal_internal__nid]": nextEpisodeId.value,
										"filter[langcode]": locale.value,
										"filter[status]": 1,
									},
								}
							)
							selectedEpisodeNid1.value = response.data.data[0].drupal_internal__nid
							if (response.data.data[0]) {
								episodeInfo.value = `- S:${response.data.data[0].field_season} E:${response.data.data[0].title}`
							}
						}
					} else if (res.length === 0) {
						time.value = 0
						await api
							.get(`${url}/jsonapi/node/episode`, {
								params: {
									"filter[field_series.id]": parentId,
									page: {
										limit: 1,
									},
								},
							})
							.then((response) => {
								if (response.data.data && response.data.data.length > 0) {
									const episodeData = response.data.data[0]
									// duration.value = extractDurationFromKwik((episodeData as Episode)?.field_kwik_data) ?? 0
									selectedEpisodeNid1.value = episodeData.attributes.drupal_internal__nid
								}
							})
					} else {
						duration.value = extractDurationFromKwik((props.item as any)?.field_kwik_data) ?? 0
						if (props.item?.type === "node--movie") {
							timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, props.item.drupal_internal__nid)
						}
						if (res[0]?.timestamp) {
							time.value = res[0]?.timestamp
							timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, props.item.drupal_internal__nid)

							duration.value = extractDurationFromKwik((res[0]?.target_entity as Episode)?.field_kwik_data) ?? 0
							time.value = res[0]?.timestamp
							// console.log("----time value", time.value, "----duration value", duration.value)
							// episodeInfo.value = ""
							if (res[0]?.target_entity_type === "episode") {
								// timestampStore.addTimestamp(res[0]?.timestamp, "node--episode", res[0]?.target_entity.drupal_internal__nid)
								episodeInfo.value = `- S:${res[0]?.target_entity?.field_season} E:${res[0]?.target_entity?.title}`

								selectedEpisodeNid1.value = res[0]?.target_entity?.drupal_internal__nid
								emit("episodeSelected", selectedEpisodeNid1.value)
							}
						}
						if (duration.value > 0 && time.value > 0) {
							timeline.value = (time.value / duration.value) * 100
						}
					}
				}
				if (
					props.item?.field_trailer_file.data !== null ||
					(props.item?.field_trailer && props.item?.field_trailer?.uri) ||
					typeof props.item?.field_trailer === "string"
				) {
					const videoUrl =
						props.item?.field_trailer_file.data !== null
							? `${url}${props.item?.field_trailer_file.uri.url}`
							: props.item?.field_trailer?.uri
							? props.item?.field_trailer?.uri
							: props.item?.field_trailer
					const res = await api.get("/api/sign-url", {
						params: {
							uuid: props.item?.id,
						},
						headers: {
							Accept: "application/vnd.api+json",
						},
					})

					teaserStore.options.src = res.data.signed_url
					// console.log("Showing video in large teaser preview:", teaserStore.options.src)

					window.initPlayer(playerId.value, teaserStore.options, () => {
						teaserStore.state.playerRef = window.kwikMotion(playerId.value)
					})
				} else {
					showVideo.value = false
					// disposePlayer()
				}
			}
		}
	)

	const handleMouseEnter = () => {
		// Clear any existing timeout
		if (hoverTimeout.value) {
			clearTimeout(hoverTimeout.value)
		}

		// Debounce the hover to prevent rapid triggers
		hoverTimeout.value = window.setTimeout(() => {
			showPreviewSection.value = true
		}, 500)
	}

	const play = async () => {
		if (isMobileDevice.value === true) {
			if (props.item?.type?.includes("movie")) {
				router.push({ name: "InnerMovie", params: { id: props.item.drupal_internal__nid } })
			} else if (props.item.type?.includes("series")) {
				router.push({ name: "InnerSeries", params: { id: props.item.drupal_internal__nid } })
			}
		} else {
			await playElement(props.item)
			teaserStore.play(
				props.item?.type.includes("movie") ? "movie" : "episode",
				props.item!,
				props.item?.type.includes("movie") ? undefined : selectedEpisodeNid.value,
				router
			)
		}
	}

	const initPlayer = async () => {
		// Prevent multiple simultaneous initializations

		if (isInitializing.value) {
			return
		}

		// Check if another player is active
		if (teaserStore.state.playerRefLarge !== playerId.value && teaserStore.state.playerRefLarge !== undefined) {
			return
		}

		isInitializing.value = true

		try {
			const res = await api.get("/api/sign-url", {
				params: {
					uuid: props.item?.id,
				},
				headers: {
					Accept: "application/vnd.api+json",
				},
			})
			if (!res.data.signed_url) {
				showVideo.value = false
				isInitializing.value = false
				return
			}
			teaserStore.options.src = res.data.signed_url

			window.initPlayer(playerId.value, teaserStore.options, async () => {
				teaserStore.state.playerRefLarge = playerId.value
				isInitializing.value = false
			})
		} catch (error) {
			console.error("Error initializing player:", error)
			isInitializing.value = false
		}
	}

	const handleMouseLeave = () => {
		// Clear any pending hover timeout
		if (hoverTimeout.value) {
			clearTimeout(hoverTimeout.value)
			hoverTimeout.value = undefined
		}

		showPreviewSection.value = false
		showVideo.value = false
	}

	// Transition hooks for preview-section height animation
	const onEnterSection = (el: Element) => {
		const element = el as HTMLElement
		element.style.height = "0"
		element.style.opacity = "0"
	}

	const imageUrl = computed(() => {
		return props.item?.field_image?.image_style_uri?.teaser_large || undefined
	})
	const emit = defineEmits<{
		(e: "episodeSelected", nid: number): void
	}>()

	const time: Ref<number | undefined> = ref(undefined)

	const onAfterEnterSection = async (el: Element) => {
		const element = el as HTMLElement
		const height = element.scrollHeight

		element.style.transition = "height 0.4s ease, opacity 0.4s ease"
		element.style.height = `${200}px`
		element.style.opacity = "1"

		// After preview-section animation starts, show the video
		setTimeout(
			() => {
				if (props.item?.field_trailer?.uri) {
					showVideo.value = true
				}
			},
			isNativeDevice ? 0 : 100
		)
	}

	const onLeaveSection = (el: Element) => {
		showVideo.value = false
		const element = el as HTMLElement
		element.style.height = `${element.scrollHeight}px`
		element.style.transition = "height 0.4s ease, opacity 0.4s ease"
		// Force reflow
		element.offsetHeight
		element.style.height = "0"
		element.style.opacity = "0"
		selectedEpisodeNid1.value = 0
	}

	const onAfterLeaveSection = (el: Element) => {
		const element = el as HTMLElement
		element.style.height = ""
		element.style.opacity = ""
		element.style.transition = ""
	}

	const userStore = useUserStore()
	const movieStore = useMovieStore()
	const muted = computed(() => {
		return userStore.state.mute
	})
	const plyrRef = ref<any>(null)
	const playerRef = ref<any>(null)
	const videoUrl = ref<null | string>(null)
	const timeline = ref(0)
	const ishovered = ref(false)
	const teaserElement = ref<HTMLElement | null>(null)
	const playerElement = ref<HTMLElement | null>(null)
	const { replicateElement } = useTeaserPreview()
	const { imageRef, currentSrc, isLoaded, observe } = useLazyImage()

	// const emit = defineEmits<{
	// 	(e: "preview-started"): void
	// 	(e: "preview-ended"): void
	// }>()

	const imageSrc = computed(() => props.item?.field_image?.image_style_uri?.teaser_large || "")

	onMounted(() => {
		if (imageSrc.value) {
			observe(imageSrc.value)
		}
	})

	// watch(
	// 	() => props.isPreviewing,
	// 	(newVal: boolean, prevVal: boolean | null) => {
	// 		if (prevVal === null) return
	// 		if (newVal && teaserElement.value) {
	// 			replicateElement(teaserElement.value, {
	// 				item: props.item,
	// 				teaserSize: "teaser_large",
	// 				onPreviewEnded: () => emit("preview-ended"),
	// 			})
	// 		}
	// 	}
	// )
	timeline.value = time.value
</script>

<style lang="scss" scoped>
	.teaser-large {
		position: relative;
		width: 100%;

		&:hover {
			background: black;
		}
	}
	img {
		width: 100%;
		display: block;
		height: 100%;
		transition: all 0.3s ease;
		object-fit: cover;
		border-radius: 10px;
		opacity: 0.5;
		cursor: pointer;

		&.lazy-loaded {
			opacity: 1;
		}
	}
</style>

<style lang="scss">
	.teaser {
		border: 1px solid rgba(255, 255, 255, 0.1);
		transition: transform 0.3s ease, box-shadow 0.3s ease;
		border-radius: 10px;
		overflow: hidden;
	}

	// .teaser-large {
	// 	&:hover {
	// 		// animation: expandLargeTeaser 0.4s ease forwards;
	// 		// border: 1px solid rgba(255, 255, 255, 0.1);

	// 		// .preview-element {
	// 		// 	&.is-web {
	// 		// 		@keyframes expandLargeTeaser {
	// 		// 			0% {
	// 		// 				// transform: scale(1);
	// 		// 				box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
	// 		// 			}
	// 		// 			50% {
	// 		// 				opacity: 1;
	// 		// 			}
	// 		// 			100% {
	// 		// 				// transform: scale(1.01);
	// 		// 				box-shadow: 0 8px 400px 100px rgba(0, 0, 0, 0.8);
	// 		// 			}
	// 		// 		}
	// 		// 	}
	// 		// }
	// 	}

	// 	// // Reverse animation when closing
	// 	// &.is-closing {
	// 	// 	animation: collapseLargeTeaser 0.3s ease forwards;

	// 	// 	.preview-element {
	// 	// 		&.is-web {
	// 	// 			@keyframes collapseLargeTeaser {
	// 	// 				0% {
	// 	// 					opacity: 1;
	// 	// 					transform: scale(1.05);
	// 	// 					box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
	// 	// 				}
	// 	// 				100% {
	// 	// 					transform: scale(1);
	// 	// 					opacity: 0;
	// 	// 					box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
	// 	// 				}
	// 	// 			}
	// 	// 		}
	// 	// 	}
	// 	// }
	// }
	// Specific styles for teaser-large previews
	.teaser-large {
		.preview-element {
			position: absolute;
			bottom: 0;
			.kwik-player {
				min-height: 350px;
				max-height: 600px;
				border-radius: 12px;
				overflow: hidden;
			}
		}

		.preview-info {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-top: 15px;
			.preview-info-duration {
				display: flex;
				flex-direction: column;
				gap: 12px;
				h2 {
					font-size: 26px;

					@include br.respond-to(1560px) {
						font-size: 22px;
					}
					@include br.respond-to(br.$desktop-medium) {
						font-size: 20px;
					}
					@include br.respond-to(br.$tablet-large) {
						font-size: 18px;
					}
				}
			}
			p {
				font-size: 13px;
			}
		}
		.video-timeline-progress {
			height: 1px;
		}
	}
</style>
